import React, { useState, useEffect } from 'react';
import { SuccessFile } from '@src/types/self';
import FileList from '@src/components/FileList';
import SupplementaryUpload from '../SupplementaryUpload';
import { EduSupplementFileList, MAX_SUPPLEMENT_FILE_NUM } from '../../consts';
import { SupplementaryFile } from '../../types';
import Url from '../../url';
import './index.scss';

interface Props {
  supplementAttachList?: SupplementaryFile[];
  mode?: 'edit' | 'view';
  onChange?: (files: SupplementaryFile[]) => void;
}

// 判断两个数组是否相等
const isArrayEqual = (arr1: SupplementaryFile[], arr2: SupplementaryFile[]) => {
  if (arr1.length !== arr2.length) return false;

  for (let i = 0; i < arr1.length; i++) {
    if (
      arr1[i].attachmentId !== arr2[i].attachmentId ||
      arr1[i].attachmentName !== arr2[i].attachmentName ||
      arr1[i].attachmentUrl !== arr2[i].attachmentUrl ||
      arr1[i].attachmentFileType !== arr2[i].attachmentFileType
    ) {
      return false;
    }
  }

  return true;
};

// 补充材料
const Supplementary: React.FC<Props> = (props) => {
  const { supplementAttachList = [], onChange, mode = 'edit' } = props;
  const [files, setFiles] = useState<SupplementaryFile[]>(supplementAttachList);

  // 获取下一个可用的attachmentType
  const getNextAvailableAttachmentType = (existingFiles: SupplementaryFile[]) => {
    // 获取已使用的类型
    const usedTypes = existingFiles?.map((file) => file.attachmentFileType);

    // 找到第一个未使用的类型
    const nextType = EduSupplementFileList.find((type) => !usedTypes.includes(type));

    return nextType;
  };

  const handleChange = (newFiles: SupplementaryFile[]) => {
    // 避免重复设置相同的状态
    if (!isArrayEqual(files, newFiles)) {
      setFiles(newFiles);
      onChange?.(newFiles);
    }
  };

  const handleSuccess = (file: SuccessFile) => {
    const { fileId, fileName, fileUrl } = file || {};
    // 新文件
    const newFiles = [...files];
    const newFile = {
      attachmentId: fileId,
      attachmentName: fileName,
      attachmentUrl: fileUrl,
      attachmentFileType: getNextAvailableAttachmentType(newFiles),
    };
    // 新文件列表
    newFiles.push(newFile);

    // 更新文件列表
    handleChange(newFiles);
  };

  const handleDelete = (index: number) => {
    // 新文件
    const newFiles = [...files];
    // 删除文件
    newFiles.splice(index, 1);
    // 更新文件列表
    handleChange(newFiles);
  };

  useEffect(() => {
    // 只有当supplementAttachList与当前files不同时才更新状态
    if (!isArrayEqual(supplementAttachList, files)) {
      setFiles(supplementAttachList);
    }
  }, [supplementAttachList]);

  return (
    <div>
      {/* 分界线 */}
      {mode === 'edit' && <div className="base-form__dividing" />}
      {/* 标题 */}
      <div className="base-form__title">补充材料</div>
      {/* 文件列表 */}
      <div className="supplementary-files-container">
        {files?.map((file, index) => {
          const { attachmentId = '', attachmentName = '', attachmentUrl = '' } = file || {};
          if (mode === 'view') {
            return <img className="preview-img" src={attachmentUrl} alt="" />;
          }

          return (
            <div key={`supplementary_files_item_${attachmentId || index}`} className="supplementary-files-item">
              <FileList
                mode={mode}
                url={attachmentUrl}
                name={attachmentName}
                // 删除文件
                onRemove={() => handleDelete(index)}
              />
            </div>
          );
        })}

        {/* 新的上传按钮，只在文件数量小于6时显示 */}
        {files?.length < MAX_SUPPLEMENT_FILE_NUM && mode === 'edit' && (
          <SupplementaryUpload
            // 上传接口不变
            url={Url.upload}
            // 上传文件
            onChange={handleSuccess}
          />
        )}
      </div>
    </div>
  );
};

export default Supplementary;
